import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { CampaignInbox } from '../components/CampaignInbox.js';
import { Badge, type Notify, type Tone, btn, btnGhost, input } from '../components/ui.js';
import { fmtWhen } from '../lib/format.js';
import {
  type MktAuditRow,
  type MktCampaign,
  createCampaign,
  deleteCampaign,
  getCampaign,
  getCampaignAudit,
  getMarketingSummary,
  listCampaigns,
  updateCampaign,
} from '../lib/internal.js';
import { useAsyncData } from '../lib/useAsyncData.js';

const STATUS_TONE: Record<string, Tone> = {
  active: 'emerald',
  paused: 'amber',
  draft: 'neutral',
};

/** The editable shape of a campaign (everything the PATCH route accepts). */
interface Draft {
  name: string;
  segment: string;
  maxTier: string;
  dailyCap: string;
  brief: string;
  fromName: string;
  fromEmail: string;
  replyTo: string;
  bcc: string;
}

function toDraft(c: MktCampaign): Draft {
  return {
    name: c.name,
    segment: c.segment ?? '',
    maxTier: c.maxTier == null ? '' : String(c.maxTier),
    dailyCap: String(c.dailyCap),
    brief: c.brief ?? '',
    fromName: c.fromName,
    fromEmail: c.fromEmail,
    replyTo: c.replyTo ?? '',
    bcc: c.bcc ?? '',
  };
}

type Filter = 'all' | 'active' | 'paused' | 'draft';

/**
 * Full-screen campaign management — STRICTLY INTERNAL (admin-only). A wide,
 * two-pane workspace to rename campaigns, pause/resume auto-drafting, retune
 * targeting + daily cap + sender identity, and delete campaigns. Launched as a
 * viewport overlay from the Marketing tab so it gets the whole screen.
 */
export function CampaignManager({
  onClose,
  notify,
  initialId,
}: {
  onClose: () => void;
  notify: Notify;
  initialId?: string | null;
}) {
  const [campaigns, setCampaigns] = useState<MktCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<MktCampaign | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  // Right-pane sub-view for the selected campaign.
  const [tab, setTab] = useState<'inbox' | 'audit' | 'settings'>('inbox');
  // Which action is in flight (drives per-button spinners); null = idle.
  const [action, setAction] = useState<string | null>(null);
  const busy = action !== null;
  // Outreach deliverability today (global, across all campaigns).
  const { data: summary } = useAsyncData(useCallback(() => getMarketingSummary(), []));

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setCampaigns(await listCampaigns());
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: load once on mount
  useEffect(() => {
    refresh();
    if (initialId) select(initialId);
  }, []);

  const select = useCallback(
    async (id: string) => {
      setSelectedId(id);
      setDetail(null);
      setDraft(null);
      try {
        const cmp = await getCampaign(id);
        setDetail(cmp);
        setDraft(toDraft(cmp));
      } catch (e) {
        notify(e instanceof Error ? e.message : 'Failed to load campaign');
      }
    },
    [notify],
  );

  // Refresh the selected campaign's live counts/status WITHOUT nulling detail —
  // so the inbox (keyed by campaign id) doesn't unmount/reload on every action.
  const reloadDetail = useCallback(async () => {
    if (!selectedId) return;
    try {
      setDetail(await getCampaign(selectedId));
    } catch {
      /* a transient refresh failure is non-fatal; the list still updates */
    }
  }, [selectedId]);

  const wrap = async (id: string, fn: () => Promise<void>, ok?: string) => {
    setAction(id);
    try {
      await fn();
      if (ok) notify(ok, 'success');
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setAction(null);
    }
  };

  const dirty = useMemo(
    () => !!detail && !!draft && JSON.stringify(toDraft(detail)) !== JSON.stringify(draft),
    [detail, draft],
  );

  const save = () =>
    wrap(
      'save',
      async () => {
        if (!detail || !draft) return;
        if (!draft.name.trim()) throw new Error('Name is required');
        await updateCampaign(detail.id, {
          name: draft.name.trim(),
          segment: draft.segment || null,
          maxTier: draft.maxTier === '' ? null : Number(draft.maxTier),
          dailyCap: Number(draft.dailyCap),
          brief: draft.brief.trim() || null,
          fromName: draft.fromName.trim(),
          fromEmail: draft.fromEmail.trim(),
          replyTo: draft.replyTo.trim() || null,
          bcc: draft.bcc.trim() || null,
        });
        await refresh();
        await select(detail.id);
      },
      'Campaign saved',
    );

  const setStatus = (next: 'active' | 'paused') =>
    wrap(
      'status',
      async () => {
        if (!detail) return;
        await updateCampaign(detail.id, { status: next });
        await refresh();
        await reloadDetail();
      },
      next === 'active' ? 'Auto-drafting ON — the cron will draft the daily batch' : 'Paused',
    );

  const create = () =>
    wrap(
      'create',
      async () => {
        const { id } = await createCampaign({
          name: 'Untitled campaign',
          segment: 'medicare',
          maxTier: 0,
          // omit dailyCap → server defaults it to the configured account max
        });
        await refresh();
        await select(id);
      },
      'Draft campaign created — edit and save',
    );

  const remove = () => {
    if (!detail) return;
    if (
      !window.confirm(
        `Delete “${detail.name}” and all of its drafts, sends, replies, and enrollments? Leads stay in the pool. This cannot be undone.`,
      )
    )
      return;
    wrap(
      'delete',
      async () => {
        await deleteCampaign(detail.id);
        setSelectedId(null);
        setDetail(null);
        setDraft(null);
        await refresh();
      },
      'Campaign deleted',
    );
  };

  const shown = campaigns.filter((c) => filter === 'all' || (c.lifecycle ?? c.status) === filter);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-neutral-50">
      {/* Screen header */}
      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-neutral-200 bg-white px-6 py-3.5">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-base font-semibold text-neutral-900">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-emerald-600 text-white">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            Campaign management
          </h2>
          <p className="text-xs text-neutral-500">
            Rename, pause/resume auto-drafting, retune targeting &amp; sender, or delete.
          </p>
        </div>
        <button type="button" className={btnGhost} onClick={onClose}>
          ✕ Close
        </button>
      </header>

      {/* Two-pane body */}
      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[380px_1fr]">
        {/* Left: campaign list */}
        <aside className="flex min-h-0 flex-col border-r border-neutral-200 bg-white">
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-neutral-100 px-4 py-3">
            <div className="flex gap-1">
              {(['all', 'active', 'paused', 'draft'] as Filter[]).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={`rounded-full px-2.5 py-1 text-xs font-medium capitalize transition ${
                    filter === f
                      ? 'bg-neutral-900 text-white'
                      : 'text-neutral-500 hover:bg-neutral-100'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
            <button type="button" className={btn} disabled={busy} onClick={create}>
              {action === 'create' ? <Spin /> : '+ New'}
            </button>
          </div>
          <ul className="min-h-0 flex-1 divide-y divide-neutral-100 overflow-y-auto">
            {loading && <li className="px-4 py-6 text-sm text-neutral-400">Loading…</li>}
            {!loading && shown.length === 0 && (
              <li className="px-4 py-6 text-sm text-neutral-400">No campaigns.</li>
            )}
            {shown.map((c) => {
              const active = selectedId === c.id;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => select(c.id)}
                    aria-pressed={active}
                    className={`w-full px-4 py-3 text-left transition ${
                      active ? 'bg-emerald-50' : 'hover:bg-neutral-50'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={`truncate text-sm font-medium ${active ? 'text-emerald-800' : 'text-neutral-800'}`}
                      >
                        {c.name}
                      </span>
                      <Badge tone={STATUS_TONE[c.lifecycle ?? c.status] ?? 'neutral'}>
                        {c.lifecycle ?? c.status}
                      </Badge>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-neutral-500">
                      <span>{c.segment ?? 'any'}</span>
                      {c.maxTier != null && <span>≤T{c.maxTier}</span>}
                      <span>cap {c.dailyCap}/day</span>
                      {(['queued', 'drafted', 'sent', 'replied'] as const).map((s) => (
                        <span key={s}>
                          <b className="text-neutral-700">{c.counts?.[s] ?? 0}</b> {s}
                        </span>
                      ))}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Right: editor */}
        <section className="min-h-0 overflow-y-auto">
          {!detail || !draft ? (
            <div className="flex h-full items-center justify-center p-10 text-center text-sm text-neutral-400">
              {selectedId ? 'Loading…' : 'Select a campaign on the left, or create a new one.'}
            </div>
          ) : (
            <div className="mx-auto max-w-[96rem] px-6 py-6">
              {/* Status + danger actions */}
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-neutral-900">{detail.name}</h3>
                  <Badge tone={STATUS_TONE[detail.lifecycle ?? detail.status] ?? 'neutral'}>
                    {detail.lifecycle ?? detail.status}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  {detail.status === 'active' ? (
                    <button
                      type="button"
                      className={btnGhost}
                      disabled={busy}
                      onClick={() => setStatus('paused')}
                    >
                      {action === 'status' ? <Spin /> : '⏸ Pause auto-drafting'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={btn}
                      disabled={busy}
                      onClick={() => setStatus('active')}
                      title="Let the 15-minute cron draft this campaign's daily batch automatically"
                    >
                      {action === 'status' ? <Spin /> : '▶ Resume auto-drafting'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="rounded-lg px-3 py-1.5 text-sm font-medium text-rose-600 transition hover:bg-rose-50 disabled:opacity-50"
                    disabled={busy}
                    onClick={remove}
                  >
                    {action === 'delete' ? <Spin /> : '🗑 Delete'}
                  </button>
                </div>
              </div>

              {/* Live stats */}
              <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                {(['queued', 'drafted', 'sent', 'replied'] as const).map((s) => (
                  <Stat key={s} label={s} value={detail.counts?.[s] ?? 0} />
                ))}
                <Stat label="sent today" value={detail.sentToday ?? 0} />
                <Stat label="left today" value={detail.remainingToday ?? 0} accent />
                {/* Deliverability today — global across all campaigns (since 00:00 UTC). */}
                <Stat
                  label="unsubs today"
                  value={summary?.today.unsubs ?? 0}
                  danger={!!summary && summary.today.unsubs > 0}
                />
                <Stat
                  label="bounced today"
                  value={summary?.today.bounced ?? 0}
                  danger={!!summary && summary.today.bounced > 0}
                />
              </div>

              {/* Sub-view: work the inbox, or edit settings */}
              <div className="mb-6 flex gap-1 border-b border-neutral-200">
                {(['inbox', 'audit', 'settings'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTab(t)}
                    className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium capitalize transition ${
                      tab === t
                        ? 'border-emerald-600 text-emerald-700'
                        : 'border-transparent text-neutral-500 hover:text-neutral-800'
                    }`}
                  >
                    {t === 'inbox' ? 'Inbox' : t === 'audit' ? 'Audit' : 'Settings'}
                  </button>
                ))}
              </div>

              {tab === 'inbox' ? (
                <CampaignInbox
                  campaign={detail}
                  notify={notify}
                  showStatusToggle={false}
                  tall
                  onChanged={() => {
                    refresh();
                    reloadDetail();
                  }}
                />
              ) : tab === 'audit' ? (
                <CampaignAudit campaignId={detail.id} />
              ) : (
                <div className="max-w-4xl">
                  {/* Targeting + cadence */}
                  <Section title="Targeting & cadence">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Field label="Name" hint="Your internal label for this campaign">
                        <input
                          className={input}
                          value={draft.name}
                          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                        />
                      </Field>
                      <Field label="Segment" hint="Line of business to enroll">
                        <select
                          className={input}
                          value={draft.segment}
                          onChange={(e) => setDraft({ ...draft, segment: e.target.value })}
                        >
                          <option value="medicare">medicare</option>
                          <option value="health_life">health_life</option>
                          <option value="group">group</option>
                          <option value="ancillary">ancillary</option>
                          <option value="">any</option>
                        </select>
                      </Field>
                      <Field label="Max tier" hint="0 = best only; 2 = tiers 0–2 (blank = any)">
                        <select
                          className={input}
                          value={draft.maxTier}
                          onChange={(e) => setDraft({ ...draft, maxTier: e.target.value })}
                        >
                          <option value="">Any tier</option>
                          <option value="0">Tier 0 (best)</option>
                          <option value="1">Up to Tier 1</option>
                          <option value="2">Up to Tier 2</option>
                          <option value="3">Up to Tier 3</option>
                        </select>
                      </Field>
                      <Field label="Daily cap" hint="Max sends per day (up to your account max)">
                        <input
                          className={input}
                          value={draft.dailyCap}
                          onChange={(e) => setDraft({ ...draft, dailyCap: e.target.value })}
                        />
                      </Field>
                    </div>
                    <Field label="Brief" hint="The value-prop / voice the AI should lead with">
                      <textarea
                        className={`${input} h-24`}
                        value={draft.brief}
                        onChange={(e) => setDraft({ ...draft, brief: e.target.value })}
                      />
                    </Field>
                  </Section>

                  {/* Sender identity */}
                  <Section title="Sender identity">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Field label="From name">
                        <input
                          className={input}
                          value={draft.fromName}
                          onChange={(e) => setDraft({ ...draft, fromName: e.target.value })}
                        />
                      </Field>
                      <Field label="From email">
                        <input
                          className={input}
                          value={draft.fromEmail}
                          onChange={(e) => setDraft({ ...draft, fromEmail: e.target.value })}
                        />
                      </Field>
                      <Field
                        label="Reply-To"
                        hint="Where replies route (the inbound worker address)"
                      >
                        <input
                          className={input}
                          value={draft.replyTo}
                          onChange={(e) => setDraft({ ...draft, replyTo: e.target.value })}
                        />
                      </Field>
                      <Field label="BCC" hint="Operator copy on every send">
                        <input
                          className={input}
                          value={draft.bcc}
                          onChange={(e) => setDraft({ ...draft, bcc: e.target.value })}
                        />
                      </Field>
                    </div>
                  </Section>

                  {/* Save bar */}
                  <div className="sticky bottom-0 -mx-6 mt-2 flex items-center justify-end gap-3 border-t border-neutral-200 bg-neutral-50/95 px-6 py-3 backdrop-blur">
                    {dirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
                    <button
                      type="button"
                      className={btnGhost}
                      disabled={busy || !dirty}
                      onClick={() => setDraft(toDraft(detail))}
                    >
                      Revert
                    </button>
                    <button type="button" className={btn} disabled={busy || !dirty} onClick={save}>
                      {action === 'save' ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Spin /> Saving…
                        </span>
                      ) : (
                        'Save changes'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

/** Small inline spinner using the current text color. */
function Spin() {
  return (
    <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent opacity-70" />
  );
}

function Stat({
  label,
  value,
  accent,
  danger,
}: { label: string; value: number; accent?: boolean; danger?: boolean }) {
  const box = danger
    ? 'border-rose-200 bg-rose-50'
    : accent
      ? 'border-emerald-200 bg-emerald-50'
      : 'border-neutral-200 bg-white';
  const text = danger ? 'text-rose-700' : accent ? 'text-emerald-700' : 'text-neutral-900';
  return (
    <div className={`rounded-xl border px-3 py-2.5 ${box}`}>
      <div className={`text-lg font-semibold ${text}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-neutral-500">{label}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-6 rounded-2xl border border-neutral-200 bg-white p-5">
      <h4 className="mb-4 text-xs font-semibold uppercase tracking-wide text-neutral-500">
        {title}
      </h4>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the form control is passed in via children
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-neutral-600">{label}</span>
      {children}
      {hint && (
        <span className="mt-0.5 block text-[11px] leading-tight text-neutral-400">{hint}</span>
      )}
    </label>
  );
}

const AUDIT_TONE: Record<MktAuditRow['status'], Tone> = {
  sent: 'sky',
  delivered: 'emerald',
  replied: 'emerald',
  bounced: 'rose',
  complaint: 'rose',
  unsubscribed: 'amber',
};

/** Flat send history for a campaign — every email that went out + its latest
 * status (sent / delivered / replied / bounced / complaint / unsubscribed). */
function CampaignAudit({ campaignId }: { campaignId: string }) {
  const { data, error, reload } = useAsyncData(
    useCallback(() => getCampaignAudit(campaignId), [campaignId]),
  );
  if (error)
    return (
      <p className="text-sm text-rose-600">
        {error}{' '}
        <button type="button" className="underline" onClick={reload}>
          Retry
        </button>
      </p>
    );
  if (!data) return <p className="text-sm text-neutral-500">Loading…</p>;
  if (data.length === 0)
    return (
      <p className="text-sm text-neutral-500">No emails have been sent for this campaign yet.</p>
    );
  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-xs text-neutral-500">
          {data.length} sent (latest 500), newest first. Status reflects the most recent signal per
          recipient.
        </p>
        <button type="button" className={btnGhost} onClick={reload}>
          Refresh
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full table-auto text-sm">
          <thead className="border-b border-neutral-200 text-left text-xs uppercase tracking-wide text-neutral-500">
            <tr>
              <th className="px-2 py-1.5">Recipient</th>
              <th className="px-2 py-1.5">Subject</th>
              <th className="px-2 py-1.5">Sent</th>
              <th className="px-2 py-1.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.id} className="border-t border-neutral-100">
                <td className="px-2 py-1.5">
                  <div className="font-medium text-neutral-800">
                    {row.company ?? row.name ?? '—'}
                  </div>
                  <div className="text-xs text-neutral-400">{row.email ?? ''}</div>
                </td>
                <td className="max-w-xs truncate px-2 py-1.5 text-neutral-600">
                  {row.subject ?? '—'}
                </td>
                <td className="whitespace-nowrap px-2 py-1.5 text-neutral-500">
                  {fmtWhen(row.sentAt)}
                </td>
                <td className="px-2 py-1.5">
                  <Badge tone={AUDIT_TONE[row.status]}>{row.status}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
